import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, Alert, Typography, Paper } from '@mui/material'
import { isEqual } from 'lodash'
import { useGlobalNavigationBlock } from './useGlobalNavigationBlock'
import GenericTextField from 'components/UserFields/GenericTextField'
import PhoneNumberField from 'components/UserFields/PhoneNumberField'
import StateField from 'components/UserFields/StateField'
import GenericCheckboxField from 'components/UserFields/GenericCheckboxField'
import { getChangedFields, handleInputChangeHelper } from '../helpers'

const BasicInformationSection = ({ formData, onSave }) => {
  const [localFormData, setLocalFormData] = useState(formData)
  const [initialData, setInitialData] = useState(formData)
  const [hasChanges, setHasChanges] = useState(false)
  useGlobalNavigationBlock(hasChanges)

  useEffect(() => {
    setLocalFormData(formData)
    setInitialData(formData)
    setHasChanges(false)
  }, [formData])

  useEffect(() => {
    if (localFormData && initialData) {
      setHasChanges(!isEqual(localFormData, initialData))
    } else {
      setHasChanges(false)
    }
  }, [localFormData, initialData])

  const handleInputChange = event => {
    handleInputChangeHelper(event, setLocalFormData)
  }

  const handleSave = () => {
    if (hasChanges) {
      const changedFields = getChangedFields(localFormData, initialData)
      if (Object.keys(changedFields).length > 0) {
        onSave({ ...changedFields, id: formData.id })
        setInitialData(localFormData)
        setHasChanges(false)
      }
    }
  }

  return (
    <Box>
      {hasChanges && (
        <Alert 
          severity='info' 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'info.main'
            }
          }}
        >
          You have unsaved changes in this section. Please save before navigating away.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <GenericTextField
              value={localFormData.email}
              disabled
              label='Email'
              name='email'
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.default'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneNumberField
              value={localFormData.phone}
              handleChange={handleInputChange}
              label='Phone Number'
              name='phone'
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.default'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <GenericTextField
              value={localFormData.city || ''}
              handleChange={handleInputChange}
              label='Current City'
              name='city'
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.default'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StateField
              value={localFormData.state || ''}
              handleChange={handleInputChange}
              label='Current State'
              name='state'
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.default'
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 2, mt: 4, fontWeight: 500 }}>
              Privacy Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <GenericCheckboxField
                  name='showEmail'
                  label='Display Email'
                  value={localFormData.showEmail}
                  handleChange={handleInputChange}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.9375rem'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {localFormData.phone && (
                  <GenericCheckboxField
                    name='showPhone'
                    label='Display Phone Number'
                    value={localFormData.showPhone}
                    handleChange={handleInputChange}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9375rem'
                      }
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant='contained'
        color='primary'
        onClick={handleSave}
        disabled={!hasChanges}
        sx={{ 
          mt: 4,
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.9375rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        }}
      >
        Save Changes
      </Button>
    </Box>
  )
}

export default BasicInformationSection
